import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AffiliateService } from '../../core/services/affiliate.service';

@Component({
  selector: 'app-affiliates-join',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <main class="min-h-screen bg-stone-900 text-stone-100 py-12 sm:py-16 px-4 sm:px-6 lg:px-8 selection:bg-amber-500 selection:text-stone-950">
      <div class="max-w-6xl mx-auto space-y-14">

        <!-- Top Hero Section -->
        <div class="text-center max-w-3xl mx-auto space-y-4">
          <div class="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-400 text-xs font-semibold uppercase tracking-widest shadow-sm">
            <span class="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
            <span>Regency Exclusive Partner Network</span>
          </div>

          <h1 class="text-3xl sm:text-5xl font-serif font-bold tracking-tight text-white leading-tight">
            Join Partner Program
          </h1>

          <p class="text-base sm:text-lg text-stone-300 font-light leading-relaxed max-w-2xl mx-auto">
            Earn <span class="text-amber-400 font-semibold">$150 flat rate commission</span> on every cleaning booking referred. Seamless tracking, 60-day attribution, and direct monthly payouts.
          </p>

          <!-- Trust Badges Bar -->
          <div class="pt-2 flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-xs font-mono uppercase tracking-wider text-stone-400">
            <div class="flex items-center gap-2">
              <span class="material-symbols-outlined text-amber-400 text-lg">payments</span>
              <span>$150 Payout / Booking</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="material-symbols-outlined text-amber-400 text-lg">cookie</span>
              <span>60-Day Cookie Window</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="material-symbols-outlined text-amber-400 text-lg">bolt</span>
              <span>Instant Activation</span>
            </div>
          </div>
        </div>

        <!-- Main 2-Column Grid: Left = How It Works & Benefits, Right = Registration Form -->
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

          <!-- Left Column: How It Works & Program Explanation -->
          <div class="lg:col-span-7 space-y-10">

            <!-- SECTION: How The Program Works (3 Simple Steps) -->
            <div class="bg-stone-800/60 border border-stone-700/60 rounded-2xl p-6 sm:p-8 backdrop-blur-sm space-y-7 shadow-xl">
              <div>
                <span class="text-xs font-bold font-mono uppercase tracking-widest text-amber-400">EFFORTLESS SETUP</span>
                <h2 class="text-2xl sm:text-3xl font-serif font-bold text-white mt-1">
                  How the Partner Program Works
                </h2>
                <p class="text-stone-300 text-sm mt-2 leading-relaxed font-light">
                  Getting started is completely frictionless with zero approval wait times. In three simple steps, you can begin generating high-ticket passive income:
                </p>
              </div>

              <!-- Step Cards -->
              <div class="space-y-4">

                <!-- Step 1 -->
                <div class="flex items-start gap-4 p-5 rounded-xl bg-stone-900/80 border border-stone-700/60 hover:border-amber-400/40 transition-colors">
                  <div class="w-11 h-11 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center shrink-0 text-amber-400 font-serif font-bold text-lg">
                    1
                  </div>
                  <div class="space-y-1">
                    <h3 class="font-serif font-semibold text-base sm:text-lg text-white flex items-center gap-2">
                      <span>Sign Up in 60 Seconds</span>
                      <span class="text-[10px] bg-amber-400/20 text-amber-400 px-2 py-0.5 rounded font-mono font-normal">Free &amp; Instant</span>
                    </h3>
                    <p class="text-stone-300 text-xs sm:text-sm font-light leading-relaxed">
                      Register with your name, email, and choose your custom referral code (e.g. <code class="text-amber-400 font-mono text-xs bg-stone-950 px-1.5 py-0.5 rounded">regencyhousecleaning.com/?via=yourname</code>). Your portal access is immediate—no vetting delays or waiting periods.
                    </p>
                  </div>
                </div>

                <!-- Step 2 -->
                <div class="flex items-start gap-4 p-5 rounded-xl bg-stone-900/80 border border-stone-700/60 hover:border-amber-400/40 transition-colors">
                  <div class="w-11 h-11 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center shrink-0 text-amber-400 font-serif font-bold text-lg">
                    2
                  </div>
                  <div class="space-y-1">
                    <h3 class="font-serif font-semibold text-base sm:text-lg text-white">
                      Share With Your Network
                    </h3>
                    <p class="text-stone-300 text-xs sm:text-sm font-light leading-relaxed">
                      Introduce Regency's white-glove, CPR-certified residential cleaning services to your clients, residential tenants, friends, or online audience. Our reliable 60-day tracking cookie ensures you receive credit even if they book weeks later.
                    </p>
                  </div>
                </div>

                <!-- Step 3 -->
                <div class="flex items-start gap-4 p-5 rounded-xl bg-stone-900/80 border border-stone-700/60 hover:border-amber-400/40 transition-colors">
                  <div class="w-11 h-11 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center shrink-0 text-amber-400 font-serif font-bold text-lg">
                    3
                  </div>
                  <div class="space-y-1">
                    <h3 class="font-serif font-semibold text-base sm:text-lg text-white flex items-center gap-2">
                      <span>Earn $150 Paid Directly</span>
                      <span class="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded font-mono font-normal">Stripe &amp; PayPal</span>
                    </h3>
                    <p class="text-stone-300 text-xs sm:text-sm font-light leading-relaxed">
                      Every completed cleaning service booked through your referral automatically credits $150 to your account. Monitor your stats live in your dashboard and receive guaranteed monthly disbursements via Stripe Connect or PayPal.
                    </p>
                  </div>
                </div>

              </div>
            </div>

            <!-- SECTION: Program Benefits Grid -->
            <div class="space-y-4">
              <h3 class="text-xl font-serif font-bold text-white">Why Partners Choose Regency</h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">

                <div class="p-5 rounded-xl bg-stone-800/40 border border-stone-700/50 space-y-2">
                  <div class="w-9 h-9 rounded-lg bg-amber-400/10 flex items-center justify-center text-amber-400">
                    <span class="material-symbols-outlined text-xl">paid</span>
                  </div>
                  <h4 class="font-serif font-semibold text-white text-base">Top-Tier $150 Payout</h4>
                  <p class="text-xs text-stone-400 font-light leading-relaxed">
                    One of the most competitive flat payouts in luxury home services. 5 referrals per month yields $750 in reliable secondary income.
                  </p>
                </div>

                <div class="p-5 rounded-xl bg-stone-800/40 border border-stone-700/50 space-y-2">
                  <div class="w-9 h-9 rounded-lg bg-amber-400/10 flex items-center justify-center text-amber-400">
                    <span class="material-symbols-outlined text-xl">insights</span>
                  </div>
                  <h4 class="font-serif font-semibold text-white text-base">Live Dashboard &amp; Analytics</h4>
                  <p class="text-xs text-stone-400 font-light leading-relaxed">
                    Access your portal 24/7 to observe real-time click volume, conversion performance, confirmed cleans, and payout status.
                  </p>
                </div>

                <div class="p-5 rounded-xl bg-stone-800/40 border border-stone-700/50 space-y-2">
                  <div class="w-9 h-9 rounded-lg bg-amber-400/10 flex items-center justify-center text-amber-400">
                    <span class="material-symbols-outlined text-xl">verified_user</span>
                  </div>
                  <h4 class="font-serif font-semibold text-white text-base">CPR-Certified Stewards</h4>
                  <p class="text-xs text-stone-400 font-light leading-relaxed">
                    Confidence for your reputation: every Regency specialist is insured, background-checked, and certified in First Aid &amp; CPR.
                  </p>
                </div>

                <div class="p-5 rounded-xl bg-stone-800/40 border border-stone-700/50 space-y-2">
                  <div class="w-9 h-9 rounded-lg bg-amber-400/10 flex items-center justify-center text-amber-400">
                    <span class="material-symbols-outlined text-xl">account_balance</span>
                  </div>
                  <h4 class="font-serif font-semibold text-white text-base">Flexible Direct Payouts</h4>
                  <p class="text-xs text-stone-400 font-light leading-relaxed">
                    Disbursements straight to your bank via Stripe Connect Express or directly to your PayPal account on a predictable Net-30 cadence.
                  </p>
                </div>

              </div>
            </div>

            <!-- SECTION: Who Excels in the Program -->
            <div class="p-6 rounded-2xl bg-amber-500/5 border border-amber-400/20 space-y-2">
              <h4 class="font-serif font-bold text-white text-sm sm:text-base flex items-center gap-2">
                <span class="material-symbols-outlined text-amber-400">groups</span>
                <span>Who Excels in the Partner Program?</span>
              </h4>
              <p class="text-xs text-stone-300 font-light leading-relaxed">
                Real estate brokers arranging move-in/move-out cleans, luxury residential concierges, building managers, interior designers, senior living advisors, lifestyle curators, and satisfied homeowners eager to recommend white-glove cleaning to peers.
              </p>
            </div>

          </div>

          <!-- Right Column: Registration Card (Sticky on desktop) -->
          <div class="lg:col-span-5 lg:sticky lg:top-8">
            <div class="bg-stone-800/90 backdrop-blur border border-stone-700/70 rounded-2xl p-6 sm:p-8 shadow-2xl space-y-6">

              <div class="border-b border-stone-700/60 pb-5">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-amber-400">
                    <span class="material-symbols-outlined text-xl">person_add</span>
                  </div>
                  <div>
                    <h3 class="font-serif font-bold text-xl text-white">Create Partner Account</h3>
                    <p class="text-xs text-stone-400">Instant activation • 100% Free to join</p>
                  </div>
                </div>
              </div>

              @if (errorMessage()) {
                <div class="p-4 rounded-lg bg-red-950/60 border border-red-800/60 text-red-300 text-sm flex items-start gap-3">
                  <span class="material-symbols-outlined text-red-400 text-xl shrink-0">error</span>
                  <span>{{ errorMessage() }}</span>
                </div>
              }

              <form (ngSubmit)="onRegister()" class="space-y-4">
                <div>
                  <label class="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-2">Full Name / Organization</label>
                  <input
                    type="text"
                    [(ngModel)]="name"
                    name="name"
                    required
                    placeholder="e.g. Victoria Sterling"
                    (ngModelChange)="onNameChange()"
                    class="w-full bg-stone-900 border border-stone-700 rounded-lg px-4 py-3 text-stone-100 placeholder-stone-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-all text-sm"
                  />
                </div>

                <div>
                  <label class="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-2">Email Address</label>
                  <input
                    type="email"
                    [(ngModel)]="email"
                    name="email"
                    required
                    placeholder="partner@example.com"
                    class="w-full bg-stone-900 border border-stone-700 rounded-lg px-4 py-3 text-stone-100 placeholder-stone-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-all text-sm"
                  />
                </div>

                <div>
                  <label class="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-2">Custom Referral Code / Slug</label>
                  <div class="flex items-center">
                    <span class="bg-stone-950 border border-r-0 border-stone-700 rounded-l-lg px-3 py-3 text-xs text-stone-400 select-none font-mono">
                      via=
                    </span>
                    <input
                      type="text"
                      [(ngModel)]="customSlug"
                      name="customSlug"
                      placeholder="victoriasterling"
                      class="w-full bg-stone-900 border border-stone-700 rounded-r-lg px-3 py-3 text-amber-300 placeholder-stone-600 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-all text-sm font-mono"
                    />
                  </div>
                  <p class="mt-1 text-xs text-stone-400 truncate">
                    Link: <code class="text-amber-400 font-mono text-xs">regencyhousecleaning.com/?via={{ customSlug || 'yourcode' }}</code>
                  </p>
                </div>

                <div>
                  <label class="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-2">Password</label>
                  <input
                    type="password"
                    [(ngModel)]="password"
                    name="password"
                    required
                    minlength="6"
                    placeholder="At least 6 characters"
                    class="w-full bg-stone-900 border border-stone-700 rounded-lg px-4 py-3 text-stone-100 placeholder-stone-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-all text-sm"
                  />
                </div>

                <div class="flex items-start gap-3 pt-2">
                  <input
                    type="checkbox"
                    [(ngModel)]="agreeTerms"
                    name="agreeTerms"
                    id="agreeTerms"
                    required
                    class="mt-1 accent-amber-400 h-4 w-4 rounded border-stone-700 bg-stone-900 cursor-pointer"
                  />
                  <label for="agreeTerms" class="text-xs text-stone-400 leading-relaxed cursor-pointer">
                    I agree to the <a routerLink="/affiliates/terms" target="_blank" class="text-amber-400 hover:underline font-semibold">Regency House Partner Agreement &amp; Payout Terms</a>.
                  </label>
                </div>

                <button
                  type="submit"
                  [disabled]="affiliateService.isLoading() || !agreeTerms"
                  class="w-full mt-3 bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold uppercase tracking-widest py-4 px-6 rounded-lg shadow-lg hover:shadow-amber-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm cursor-pointer"
                >
                  @if (affiliateService.isLoading()) {
                    <span class="material-symbols-outlined animate-spin text-base">sync</span>
                    <span>Creating Partner Account...</span>
                  } @else {
                    <span>Create Partner Account</span>
                    <span class="material-symbols-outlined text-base">arrow_forward</span>
                  }
                </button>
              </form>

              <div class="pt-5 border-t border-stone-700/60 text-center">
                <p class="text-xs text-stone-400">
                  Already a partner?
                  <a routerLink="/affiliates/login" class="text-amber-400 hover:text-amber-300 font-semibold ml-1 underline">
                    Sign in to Portal
                  </a>
                </p>
              </div>

            </div>
          </div>

        </div>

        <!-- FAQ Section -->
        <div class="bg-stone-800/40 border border-stone-700/50 rounded-2xl p-6 sm:p-10 space-y-6">
          <div class="text-center max-w-2xl mx-auto space-y-2">
            <h3 class="text-2xl font-serif font-bold text-white">Frequently Asked Questions</h3>
            <p class="text-xs text-stone-400 font-light">Everything you need to know about payouts, tracking, and referrals.</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5 pt-3 text-sm font-light">
            <div class="space-y-1.5 p-4 rounded-xl bg-stone-900/60 border border-stone-800">
              <h4 class="font-serif font-semibold text-white">How much do I earn per referral?</h4>
              <p class="text-stone-400 text-xs leading-relaxed">
                You receive a guaranteed flat $150 commission for every client who books and completes a cleaning service using your referral link.
              </p>
            </div>

            <div class="space-y-1.5 p-4 rounded-xl bg-stone-900/60 border border-stone-800">
              <h4 class="font-serif font-semibold text-white">When and how do I receive payouts?</h4>
              <p class="text-stone-400 text-xs leading-relaxed">
                Commissions are released on a standard Net-30 monthly cycle directly to your bank account via Stripe Connect Express or your PayPal account.
              </p>
            </div>

            <div class="space-y-1.5 p-4 rounded-xl bg-stone-900/60 border border-stone-800">
              <h4 class="font-serif font-semibold text-white">How does 60-day cookie tracking work?</h4>
              <p class="text-stone-400 text-xs leading-relaxed">
                When a visitor clicks your link, our system securely remembers them for 60 days. Even if they browse around and book weeks later, you earn full credit.
              </p>
            </div>

            <div class="space-y-1.5 p-4 rounded-xl bg-stone-900/60 border border-stone-800">
              <h4 class="font-serif font-semibold text-white">Is there any fee or cap on earnings?</h4>
              <p class="text-stone-400 text-xs leading-relaxed">
                Joining is 100% free with no hidden charges. There are zero caps or limits—refer 10 clients a month and earn $1,500 effortlessly.
              </p>
            </div>
          </div>
        </div>

      </div>
    </main>
  `,
})
export class AffiliatesJoinComponent {
  protected readonly affiliateService = inject(AffiliateService);
  private readonly router = inject(Router);

  name = '';
  email = '';
  password = '';
  customSlug = '';
  agreeTerms = false;
  errorMessage = signal<string | null>(null);

  onNameChange(): void {
    if (!this.customSlug || this.customSlug === this.name.toLowerCase().slice(0, -1).replace(/[^a-z0-9]/g, '')) {
      this.customSlug = this.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    }
  }

  onRegister(): void {
    if (!this.agreeTerms) {
      this.errorMessage.set('Please accept the partner agreement terms to continue.');
      return;
    }
    this.errorMessage.set(null);

    this.affiliateService.register({
      name: this.name,
      email: this.email,
      password: this.password,
      customSlug: this.customSlug || undefined,
    }).subscribe({
      next: () => {
        this.router.navigate(['/affiliates/dashboard']);
      },
      error: (err) => {
        this.errorMessage.set(err.message || 'Registration failed');
      },
    });
  }
}
